import React from 'react';
import { KeytipManager } from '@fluentui/react';
import { addDocumentEventListener } from 'owa-event-listener';
import { shouldShowListView } from 'owa-mail-layout';
import { getListViewState, getSelectedTableView, type TableView } from 'owa-mail-list-store';
import shouldAllowTypeToJump from '../utils/shouldAllowTypeToJump';

const MAIL_PANE_SELECTOR = '[data-mail-pane="true"]';
const RESERVED_TYPING_TARGETS =
    'input, textarea, select, [contenteditable]:not([contenteditable="false"]), ' +
    '[role="textbox"], [role="searchbox"], [role="combobox"], [role="menu"], ' +
    '[role="menubar"], [role^="menuitem"], [role="dialog"], [role="alertdialog"], ' +
    '[aria-haspopup][aria-expanded="true"]';
const TYPEAHEAD_TARGETS = '[role="listbox"], [role="tree"], [role="grid"]';

function isReservedTypingTarget(target: Element): boolean {
    if (target.closest(RESERVED_TYPING_TARGETS)) {
        return true;
    }

    const typeaheadOwner = target.closest(TYPEAHEAD_TARGETS);
    return (
        !!typeaheadOwner &&
        typeaheadOwner !== target.closest('#MailList')?.querySelector('[role="listbox"]')
    );
}

export default function useMailListTypeToJump(
    tableView: TableView | undefined,
    onOpen: () => void
): void {
    const isEnabled = shouldAllowTypeToJump(tableView) && shouldShowListView();

    React.useEffect(() => {
        if (!isEnabled || !tableView) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            const isAltGraph = event.getModifierState('AltGraph');
            const originalTarget = event.composedPath()[0];
            if (
                event.defaultPrevented ||
                event.repeat ||
                event.isComposing ||
                event.keyCode === 229 ||
                event.metaKey ||
                ((event.ctrlKey || event.altKey) && !isAltGraph) ||
                (event.key !== 'Dead' && Array.from(event.key).length !== 1) ||
                event.key === ' ' ||
                !(event.target instanceof Element) ||
                event.target !== document.activeElement ||
                !event.target.closest(MAIL_PANE_SELECTOR) ||
                isReservedTypingTarget(event.target) ||
                (originalTarget instanceof Element && isReservedTypingTarget(originalTarget)) ||
                getSelectedTableView() !== tableView ||
                !shouldAllowTypeToJump(tableView) ||
                !shouldShowListView() ||
                KeytipManager.getInstance().inKeytipMode ||
                getListViewState().itemContextMenuState ||
                getListViewState().isFocusWithinJumpTo
            ) {
                return;
            }

            // Focus the existing input in this keydown; the browser inserts the character.
            onOpen();
        };

        // Descendant shortcut handlers retain first use of the event.
        return addDocumentEventListener('MailListTypeToJump', 'keydown', onKeyDown);
    }, [isEnabled, tableView, onOpen]);
}
